import { MenuItem } from "../types/menuItem";
import { apiClient } from "./client";

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

// API response structure from backend
interface ApiMenuItemResponse {
  menuitemid: string;
  hotelid: string;
  restaurantmenuid: string;
  photo: string | null;
  description: string | null;
  ingredients: string | null;
  category: string | null;
  mastersection: string | null;
  itemname: string;
  spicelevel: string | null;
  price: {
    priceId: string;
    amount: string;
    currencyCode: string;
    priceType: string | null;
    description: string | null;
  } | null;
  priceAmount: string;
  currencyCode: string;
  isspecial: boolean;
  specialstartdate: string | null;
  specialenddate: string | null;
  createdat: string;
  updatedat: string;
}

interface ApiRestaurantResponse {
  restaurantid: string;
  hotelid: string;
  name: string;
  description: string;
  phone: string;
  email: string;
  capacity: number;
  link: string;
  menucount: number;
  headerphoto: string;
}

interface ApiMenuResponse {
  restaurantmenuid: string;
  restaurantid: string;
  menuname: string;
  isroomservice: boolean;
  createdat: string;
  updatedat: string;
}

interface ApiPriceResponse {
  priceId: string;
  hotelId: string;
  hotelName: string;
  amount: string;
  currency: {
    currencyId: string;
    code: string;
    symbol: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Restaurant {
  restaurantId: string;
  hotelId: string;
  name: string;
  description: string;
  phone: string;
  email: string;
  capacity: number;
  link: string;
  menuCount: number;
  headerPhoto: string;
}

export interface Menu {
  restaurantMenuId: string;
  restaurantId: string;
  menuName: string;
  isRoomService: boolean;
  createdAt: string;
  updatedAt: string;
}

// START_ADDITION
export interface ApiMenuScheduleEntry {
  menuoperatingscheduleid: string;
  restaurantmenuid: string;
  intervalid: string | null; // Assuming intervalid can be null if not joined or not set
  dayofweek: string;
  scheduletype: string | null;
  isactive: boolean;
  notes: string | null;
  startTime: string | null; // From joined scheduleinterval, can be null
  endTime: string | null; // From joined scheduleinterval, can be null
  intervalDescription: string | null; // From joined scheduleinterval
}

export interface MenuSchedule {
  menuOperatingScheduleId: string;
  restaurantMenuId: string;
  intervalId: string | null;
  dayOfWeek: string;
  scheduleType: string | null;
  isActive: boolean;
  notes: string | null;
  startTime: string | null;
  endTime: string | null;
  intervalDescription: string | null;
}

// END_ADDITION

// Helper function to transform API response to match our expected structure
const transformMenuItem = (apiItem: ApiMenuItemResponse): MenuItem => {
  let finalPriceId: string = "";
  let finalPriceAmount: number = 0;
  let finalCurrencyCode: string = "USD";

  if (apiItem.price) {
    // Primary source: the price object
    finalPriceId = apiItem.price.priceId;
    if (apiItem.price.amount !== null && apiItem.price.amount !== undefined) {
      const parsedAmount = parseFloat(apiItem.price.amount);
      finalPriceAmount = isNaN(parsedAmount) ? 0 : parsedAmount;
    }
    finalCurrencyCode = apiItem.price.currencyCode;
  } else if (
    apiItem.priceAmount !== null &&
    apiItem.priceAmount !== undefined
  ) {
    // Fallback to top-level fields if price object is null
    // This assumes apiItem.priceAmount is the numeric string value and apiItem.currencyCode is its currency.
    // MenuItem.price (the ID) will be empty in this case as there's no priceId from a price object.
    const parsedAmount = parseFloat(apiItem.priceAmount);
    finalPriceAmount = isNaN(parsedAmount) ? 0 : parsedAmount;

    if (apiItem.currencyCode) {
      finalCurrencyCode = apiItem.currencyCode;
    }
    // finalPriceId remains '' as per initialization
  }

  return {
    menuitemid: apiItem.menuitemid,
    hotelid: apiItem.hotelid,
    restaurantmenuid: apiItem.restaurantmenuid,
    photo: apiItem.photo,
    description: apiItem.description,
    ingredients: apiItem.ingredients,
    category: apiItem.category,
    itemname: apiItem.itemname,
    spicelevel: null,
    price: finalPriceId, // This is the Price ID string for MenuItem
    priceAmount: finalPriceAmount, // This is the parsed number for MenuItem
    currencyCode: finalCurrencyCode,
    isspecial: false,
    specialstartdate: null,
    specialenddate: null,
    createdat: apiItem.createdat,
    updatedat: apiItem.updatedat,
  };
};

// Helper function to transform restaurant API response
const transformRestaurant = (restaurant: ApiRestaurantResponse): Restaurant => {
  return {
    restaurantId: restaurant.restaurantid,
    hotelId: restaurant.hotelid,
    name: restaurant.name,
    description: restaurant.description,
    phone: restaurant.phone,
    email: restaurant.email,
    capacity: restaurant.capacity,
    link: restaurant.link,
    menuCount: restaurant.menucount,
    headerPhoto: restaurant.headerphoto,
  };
};

export const getRestaurants = async (
  callApi: (config: any) => Promise<any>,
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<Restaurant>> => {
  const response = await callApi({
    method: "GET",
    url: `/management/hotel/restaurants`,
    params: {
      page,
      limit,
    },
  });

  // The API returns { data: Restaurant[] }
  const restaurants = response.data.data;

  return {
    data: restaurants.map(transformRestaurant),
    pagination: {
      page,
      limit,
      total: restaurants.length,
      totalPages: Math.ceil(restaurants.length / limit),
    },
  };
};

export const getMenus = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string
): Promise<Menu[]> => {
  const response = await callApi({
    method: "GET",
    url: `/management/hotel/restaurants/${restaurantId}/menus`,
  });

  // The API returns { data: Menu[] }
  const menus = response.data.data;

  return menus.map((menu: ApiMenuResponse) => ({
    restaurantMenuId: menu.restaurantmenuid,
    restaurantId: menu.restaurantid,
    menuName: menu.menuname,
    isRoomService: menu.isroomservice,
    createdAt: menu.createdat,
    updatedAt: menu.updatedat,
  }));
};

export const getPrice = async (
  priceId: string,
  hotelId: string
): Promise<{ amount: number; currencyCode: string }> => {
  const response = await apiClient.get<ApiPriceResponse>(
    `/management/prices/${priceId}`,
    {
      params: { hotelId },
    }
  );
  return {
    amount: parseFloat(response.data.amount),
    currencyCode: response.data.currency.code,
  };
};

export const getMenuItems = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  page: number = 1,
  limit: number = 20,
  category?: string
): Promise<PaginatedResponse<MenuItem>> => {
  const response = await callApi({
    method: "GET",
    url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/items`,
    params: {
      page,
      limit,
      ...(category && { category }),
    },
  });

  const apiItems = response.data.data; // apiItems is ApiMenuItemResponse[]

  // The items in apiItems are already ApiMenuItemResponse.
  // transformMenuItem will handle converting string prices to numbers.
  // The previous itemsWithPrices mapping that called getPrice is removed.

  return {
    data: apiItems.map((apiItem: ApiMenuItemResponse) =>
      transformMenuItem(apiItem)
    ),
    pagination: {
      page,
      limit,
      total: apiItems.length, // Use apiItems.length for total
      totalPages: Math.ceil(apiItems.length / limit),
    },
  };
};

export const getMenuItem = async (
  hotelId: string,
  restaurantId: string,
  menuId: string,
  itemId: string
): Promise<MenuItem> => {
  const response = await apiClient.get<ApiResponse<ApiMenuItemResponse[]>>(
    `/management/hotel/${hotelId}/restaurants/${restaurantId}/menus/${menuId}/items`,
    {
      params: { itemId },
    }
  );
  const itemsArray = response.data.data;
  const itemResponse = itemsArray.find((i) => i.menuitemid === itemId);

  if (!itemResponse) {
    throw new Error(`Menu item ${itemId} not found`);
  }
  return transformMenuItem(itemResponse);
};

export const createMenuItem = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  item: Omit<
    MenuItem,
    "menuitemid" | "hotelid" | "restaurantmenuid" | "createdat" | "updatedat"
  >
): Promise<MenuItem> => {
  if (import.meta.env.DEV)
    console.log("createMenuItem called with:", {
      restaurantId,
      menuId,
      item,
    });

  try {
    // First, create a price for the item since price ID is required
    const priceAmount = item.priceAmount || 0;

    const priceResponse = await callApi({
      method: "POST",
      url: `/management/prices`,
      data: {
        amount: priceAmount.toString(),
        currencyCode: item.currencyCode || "USD",
        priceType: "Menu Item",
        description: `Price for ${item.itemname}`,
      },
    });

    const createdPriceId = priceResponse.data?.priceId;
    if (!createdPriceId) {
      throw new Error("Failed to create price for menu item");
    }

    // Now create the menu item with the price ID
    const payload = {
      itemname: item.itemname,
      description: item.description || null,
      ingredients: item.ingredients || null,
      category: item.category || null,
      photo: item.photo || null,

      price: createdPriceId, // Use the created price ID
    };

    const response = await callApi({
      method: "POST",
      url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/items`,
      data: payload,
    });

    if (import.meta.env.DEV)
      console.log(
        "Menu item created via POST, backend response raw data:",
        response.data.data
      );

    return transformMenuItem(response.data.data);
  } catch (error) {
    console.error("Error creating menu item:", error);
    throw error;
  }
};

export const updateMenuItem = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  itemId: string,
  item: Partial<MenuItem>
): Promise<MenuItem> => {
  // First, get the current item to get the price ID
  const currentResult = await getMenuItems(callApi, restaurantId, menuId);
  const currentItem = currentResult.data.find((i) => i.menuitemid === itemId);

  if (!currentItem) {
    throw new Error(`Menu item ${itemId} not found`);
  }

  // If price amount or currency is being updated, update the price separately
  if (
    (item.priceAmount !== undefined || item.currencyCode !== undefined) &&
    currentItem.price
  ) {
    await callApi({
      method: "PUT",
      url: `/management/prices/${currentItem.price}`,
      data: {
        amount: (item.priceAmount !== undefined
          ? item.priceAmount
          : currentItem.priceAmount
        ).toString(),
        currencyCode:
          item.currencyCode !== undefined
            ? item.currencyCode
            : currentItem.currencyCode,
      },
    });
  }

  // Prepare menu item update data (excluding price fields)
  const menuItemUpdateData: any = {};
  if (item.itemname !== undefined) menuItemUpdateData.itemname = item.itemname;
  if (item.description !== undefined)
    menuItemUpdateData.description = item.description;
  if (item.ingredients !== undefined)
    menuItemUpdateData.ingredients = item.ingredients;
  if (item.category !== undefined) menuItemUpdateData.category = item.category;
  if (item.photo !== undefined) menuItemUpdateData.photo = item.photo;

  // Update the menu item if there are non-price fields to update
  if (Object.keys(menuItemUpdateData).length > 0) {
    await callApi({
      method: "PUT",
      url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/items/${itemId}`,
      data: menuItemUpdateData,
    });
  }

  // Instead of fetching all items again (which would reorder them),
  // construct the updated item by merging the current item with the updates
  const updatedItem: MenuItem = {
    ...currentItem,
    ...item,
    // Ensure we update the timestamp
    updatedat: new Date().toISOString(),
  };

  return updatedItem;
};

export const deleteMenuItem = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  itemId: string
): Promise<void> => {
  await callApi({
    method: "DELETE",
    url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/items/${itemId}`,
  });
};

// Get menu items by category
export const getMenuItemsByCategory = async (
  hotelId: string,
  restaurantId: string,
  menuId: string,
  category: string
): Promise<MenuItem[]> => {
  const response = await apiClient.get<ApiResponse<MenuItem[]>>(
    `/management/hotel/${hotelId}/restaurants/${restaurantId}/menus/${menuId}/items`,
    {
      params: { category },
    }
  );
  return response.data.data;
};

// Get menu items by search query
export const searchMenuItems = async (
  hotelId: string,
  restaurantId: string,
  menuId: string,
  query: string
): Promise<MenuItem[]> => {
  const response = await apiClient.get<ApiResponse<MenuItem[]>>(
    `/management/hotel/${hotelId}/restaurants/${restaurantId}/menus/${menuId}/items`,
    {
      params: { search: query },
    }
  );
  return response.data.data;
};

export const updateMenuItemPrice = async (
  hotelId: string,
  restaurantId: string,
  menuId: string,
  itemId: string,
  amount: number,
  currencyCode: string
): Promise<MenuItem> => {
  // Get the current menu item to get the price ID
  const currentItem = await getMenuItem(hotelId, restaurantId, menuId, itemId);

  // Update the existing price
  await apiClient.put<ApiPriceResponse>(
    `/management/prices/${currentItem.price}`,
    {
      hotelId,
      amount: amount.toString(),
      currencyCode,
    }
  );

  // Get the updated menu item
  return getMenuItem(hotelId, restaurantId, menuId, itemId);
};

// Menu schedule API functions
export const getMenuSchedules = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string
): Promise<MenuSchedule[]> => {
  if (!restaurantId || !menuId) {
    console.warn("Restaurant ID or Menu ID is missing for getMenuSchedules");
    return [];
  }

  try {
    const response = await callApi({
      method: "GET",
      url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/schedules`,
    });

    // Transform the response to match our expected interface
    // Backend returns starttime and endtime at top level (from joined scheduleinterval table)
    const transformedSchedules = (response.data.data || []).map(
      (schedule: {
        scheduleId: string;
        menuId: string;
        intervalId: string;
        dayOfWeek: string;
        scheduleType: string;
        isActive: boolean;
        notes: string | null;
        startTime?: string;
        endTime?: string;
        starttime?: string; // Backend may return snake_case
        endtime?: string; // Backend may return snake_case
        intervalDescription?: string | null;
      }) => ({
        menuOperatingScheduleId: schedule.scheduleId,
        restaurantMenuId: schedule.menuId,
        intervalId: schedule.intervalId,
        dayOfWeek: schedule.dayOfWeek,
        scheduleType: schedule.scheduleType,
        isActive: schedule.isActive,
        notes: schedule.notes,
        // Handle both camelCase and snake_case from backend
        startTime: schedule.startTime || schedule.starttime || null,
        endTime: schedule.endTime || schedule.endtime || null,
        intervalDescription: schedule.intervalDescription || null,
      })
    );

    return transformedSchedules;
  } catch (error) {
    console.error("Error fetching menu schedules:", error);
    throw error;
  }
};

export const createMenuSchedule = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  scheduleData: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
    scheduleType?: string;
    isActive?: boolean;
    notes?: string | null;
  }
): Promise<void> => {
  await callApi({
    method: "POST",
    url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/schedules`,
    data: {
      dayofweek: scheduleData.dayOfWeek,
      startTime: scheduleData.startTime,
      endTime: scheduleData.endTime,
      scheduletype: scheduleData.scheduleType || "Availability",
      isactive:
        scheduleData.isActive !== undefined ? scheduleData.isActive : true,
      notes: scheduleData.notes || null,
    },
  });
};

export const updateMenuSchedule = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  scheduleId: string,
  scheduleData: {
    dayOfWeek?: string;
    startTime?: string;
    endTime?: string;
    scheduleType?: string;
    isActive?: boolean;
    notes?: string | null;
  }
): Promise<void> => {
  await callApi({
    method: "PUT",
    url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/schedules/${scheduleId}`,
    data: {
      ...(scheduleData.dayOfWeek && { dayofweek: scheduleData.dayOfWeek }),
      ...(scheduleData.startTime && { startTime: scheduleData.startTime }),
      ...(scheduleData.endTime && { endTime: scheduleData.endTime }),
      ...(scheduleData.scheduleType && {
        scheduletype: scheduleData.scheduleType,
      }),
      ...(scheduleData.isActive !== undefined && {
        isactive: scheduleData.isActive,
      }),
      ...(scheduleData.notes !== undefined && { notes: scheduleData.notes }),
    },
  });
};

export const deleteMenuSchedule = async (
  callApi: (config: any) => Promise<any>,
  restaurantId: string,
  menuId: string,
  scheduleId: string
): Promise<void> => {
  await callApi({
    method: "DELETE",
    url: `/management/hotel/restaurants/${restaurantId}/menus/${menuId}/schedules/${scheduleId}`,
  });
};
